import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { X } from 'lucide-react';
import Logo from 'assets/logo.png';
import { useProductSearch } from '../api/searchProducts';
import { useNavigate } from 'react-router-dom';

export const Search = () => {
  const [query, setQuery] = useState('');

  // Simple Query Hook
  const { data, isLoading } = useProductSearch({ query });

  const navigate = useNavigate();

  // Flatten results
  const results = data?.products || [];

  const fadeUp = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.6 } },
  };

  return (
    <div className="min-h-screen bg-[#FFF6F2] text-gray-800 flex flex-col">
      {/* ===== Header Bar ===== */}
      <header className="flex items-center justify-between px-6 sm:px-12 py-6 border-b border-gray-300">
        <a href="/">
          <img src={Logo} alt="Ayinza Logo" className="h-10 sm:h-12" />
        </a>

        <a
          href="/"
          className="text-gray-700 hover:text-[#AC3F1E] transition flex items-center space-x-1"
        >
          <X size={26} />
          <span className="hidden sm:inline text-sm uppercase tracking-wide">Close</span>
        </a>
      </header>

      {/* ===== Search Section ===== */}
      <main className="flex flex-col items-center justify-start flex-grow px-6 sm:px-12 py-16">
        <motion.div
          className="w-full max-w-2xl text-center"
          variants={fadeUp}
          initial="hidden"
          animate="visible"
        >
          <input
            type="text"
            placeholder="Search for furniture, woodwork, or decor..."
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            className="w-full border-b-2 border-gray-400 bg-transparent text-gray-800 text-xl sm:text-2xl px-4 py-3 focus:outline-none focus:border-[#AC3F1E] placeholder:italic placeholder:text-gray-500"
          />
        </motion.div>

        {/* ===== Results ===== */}
        <motion.div
          className="w-full max-w-6xl mt-16 grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-10"
          initial="hidden"
          animate="visible"
          transition={{ staggerChildren: 0.1 }}
        >
          {/* Idle State */}
          {query.length === 0 && (
            <motion.p variants={fadeUp} className="text-gray-500 text-center col-span-full">
              Start typing to explore Ayinza’s handcrafted collection.
            </motion.p>
          )}

          {/* Loading */}
          {isLoading && query.length > 0 && (
            <motion.p variants={fadeUp} className="text-gray-500 text-center col-span-full">
              Searching…
            </motion.p>
          )}

          {/* No Results */}
          {query.length > 0 && !isLoading && results.length === 0 && (
            <motion.p variants={fadeUp} className="text-gray-500 text-center col-span-full">
              No results found for <span className="text-[#AC3F1E]">"{query}"</span>
            </motion.p>
          )}

          {/* Result Items */}
          {results.map((product) => (
            <motion.button
              key={product.slug}
              variants={fadeUp}
              className="flex flex-col items-center text-center group"
              onClick={() => navigate(`/products/${product.slug}`)}
            >
              <div className="w-60 h-60 overflow-hidden rounded-md shadow-md">
                <img
                  src={product.image}
                  alt={product.title}
                  className="w-full h-full object-cover transform transition-transform duration-500 group-hover:scale-105"
                />
              </div>

              <p className="mt-4 text-base font-medium text-gray-800">{product.title}</p>
            </motion.button>
          ))}
        </motion.div>
      </main>
    </div>
  );
};
